import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { getBuyButtonDisplayTypeForPaidVideo } from '@wix/wix-vod-shared/common';
import { StatisticLogger } from '@wix/wix-vod-shared/components';
import getPlayerTranslations from '../../../utils/get-player-translations';
import isRTL from '../../../utils/is-rtl';

import { logBi, logSharedBi } from '../../../bi';
import Cover from '../../../components/cover/cover';

import WatchOnSite from './watch-on-site/watch-on-site';
import ShareButton from './share-button/share-button';
import ShareOverlay from './share-overlay/share-overlay';

import styles from './styles.scss';
import { fedopsInteractions } from '../../../services/fedops-logger';
import { VodPlayerWithReceiver as VODPlayer } from '../wrap-with-receiver/wrap-with-receiver';

class Mobile extends React.Component {
  static propTypes = {
    dataHook: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    videoItem: PropTypes.object,
    channelData: PropTypes.object,
    instanceInfo: PropTypes.object,
    vidiUrls: PropTypes.array,
    siteUrl: PropTypes.string,
    posterUrl: PropTypes.string,
    correlationId: PropTypes.string.isRequired,

    canShowTitle: PropTypes.bool,
    canShareVideo: PropTypes.bool,
    logo: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
    isLive: PropTypes.bool,
    isOverQuota: PropTypes.bool,

    goToSite: PropTypes.func,
    goToSiteWithShare: PropTypes.func,
    goToSiteWithPurchase: PropTypes.func,
    onEnd: PropTypes.func,
  };

  constructor(props) {
    super(props);

    this.state = {
      isShareOverlayVisible: false,
      isVideoPlayRequested: false,
      isRTLEnabled: isRTL(),
      playHasBeenStarted: false,
      shouldSendStatistic: false,
    };
  }

  onEnd = () => {
    this.stopSendingStatistic();
    this.setState({
      isVideoPlayRequested: false,
      isShareOverlayVisible: false,
      playHasBeenStarted: false,
    });

    if (this.props.onEnd) {
      this.props.onEnd();
    }
  };

  onFullScreenChange = (isInFullScreen) => {
    if (!isInFullScreen) {
      this.setState({ isVideoPlayRequested: false });
    } else {
      logSharedBi('widget.vid.fullScreen.entered', {
        origin: 'embed',
      });
    }
  };

  onPlayClick = () => {
    const { videoItem } = this.props;

    if (videoItem.memberOnly) {
      this.props.goToSite();
    } else {
      this.startPlaying();
    }
  };

  startPlaying = () => {
    const { isOverQuota, correlationId } = this.props;

    fedopsInteractions.playVideoMobile.start();

    logBi('widget.embed.vid.play.clicked', {
      correlationId,
      videoType: 'computer',
      videoWidth: window.innerWidth,
      videoHeight: window.innerHeight,
      overQuoted: isOverQuota,
    });

    this.setState({ isVideoPlayRequested: true, playHasBeenStarted: false });
  };

  onPlayRequested = () => {
    const { isVideoPlayRequested } = this.state;

    if (!isVideoPlayRequested) {
      this.onPlayClick();
    }
  };

  showShareOverlay = () => {
    this.setState({
      isShareOverlayVisible: true,
    });
  };

  closeShareOverlay = () => {
    this.setState({
      isShareOverlayVisible: false,
    });
  };

  onBuyRentButtonClick = () => {
    this.sendBuyButtonClickedBi();
    this.props.goToSiteWithPurchase();
  };

  sendBuyButtonClickedBi = () => {
    logSharedBi('widget.vid.buy.clicked', {
      button: 'main',
      buyType: getBuyButtonDisplayTypeForPaidVideo(this.props.videoItem),
    });
  };

  onWatchOnSiteClick = () => {
    logBi('widget.embed.vid.goToSite.click', { buttonName: 'logo' });
    this.props.goToSite();
  };

  onMoreClick = () => {
    logBi('widget.embed.vid.goToSite.click', { buttonName: 'more' });
    this.props.goToSiteWithShare();
  };

  onPlayStart = () => {
    const { playHasBeenStarted } = this.state;
    const { correlationId } = this.props;

    if (!playHasBeenStarted) {
      fedopsInteractions.playVideoMobile.end();

      logBi('widget.embed.vid.start.play', {
        correlationId,
        videoType: 'computer',
        videoWidth: window.innerWidth,
        videoHeight: window.innerHeight,
      });
      /*
        In case when play was triggered by post message(from another app) we still
        want to hide overlay and run video, but now we have the question.
        Should we just call onPlayRequested(problem with BI and redirect to site for non authenticated users)
        or we need just to toggle isVideoPlayRequested or we need to do nothing when user not a member
      */
      this.setState({ playHasBeenStarted: true, isVideoPlayRequested: true });
    }

    this.startSendingStatistic();
  };

  startSendingStatistic = () => {
    this.setState({
      shouldSendStatistic: true,
    });
  };

  stopSendingStatistic = () => {
    this.setState({
      shouldSendStatistic: false,
    });
  };

  logStatistic = (data) => {
    const { correlationId } = this.props;
    logBi('player.debug.info', { ...data, correlationId });
  };

  render() {
    const {
      isShareOverlayVisible,
      isVideoPlayRequested,
      isRTLEnabled,
      shouldSendStatistic,
    } = this.state;

    const {
      dataHook,
      videoItem,
      channelData,
      width,
      height,
      logo,
      siteUrl,
      posterUrl,
      canShowTitle,
      canShareVideo,
      vidiUrls,
      instanceInfo,
      isLive,
    } = this.props;

    return (
      <div data-hook={dataHook} className={styles.container}>
        {!isVideoPlayRequested && (
          <Cover
            iconClassName={styles.icon}
            titleClassName={styles.title}
            posterUrl={posterUrl}
            title={videoItem.title}
            videoItem={videoItem}
            channelData={channelData}
            width={width}
            height={height}
            canShowTitle={canShowTitle}
            onBuyClicked={this.onBuyRentButtonClick}
            onPlayClicked={this.onPlayClick}
            isMobile
          >
            <div>
              <WatchOnSite
                onClick={this.onWatchOnSiteClick}
                logoUrl={logo.src}
              />
              {canShareVideo && <ShareButton onClick={this.showShareOverlay} />}
              {canShareVideo && (
                <ShareOverlay
                  isVisible={isShareOverlayVisible}
                  onClose={this.closeShareOverlay}
                  videoItem={videoItem}
                  channelData={channelData}
                  instanceInfo={instanceInfo}
                  onMoreClicked={this.onMoreClick}
                  siteUrl={siteUrl}
                  isRTL={isRTLEnabled}
                />
              )}
            </div>
          </Cover>
        )}

        {VODPlayer && (
          <div
            aria-hidden={!isVideoPlayRequested}
            className={classNames({ [styles.hidden]: !isVideoPlayRequested })}
          >
            <VODPlayer
              {...this.props}
              dataHook="internal-player"
              preload="none"
              canFocus={false}
              mobileMode
              vidiUrls={vidiUrls}
              width={window.innerWidth}
              isVideoPlayRequested={isVideoPlayRequested}
              playAtFullScreen={isVideoPlayRequested}
              texts={getPlayerTranslations()}
              canShowTitle={false}
              canShowBuyRentButton={false}
              canShowFullInfo={false}
              canShareVideo={false}
              logo={null}
              onPlay={this.onPlayStart}
              onPause={this.stopSendingStatistic}
              onFullScreenChanged={this.onFullScreenChange}
              onEnded={this.onEnd}
              onPlayRequested={this.onPlayRequested}
            >
              <StatisticLogger
                videoID={videoItem.id}
                isEmbed
                logFunction={this.logStatistic}
                channelID={channelData.id}
                shouldSendBI={shouldSendStatistic}
                isLive={isLive}
              />
            </VODPlayer>
          </div>
        )}
      </div>
    );
  }
}

export default Mobile;
